// Định dạng số, tháng, ngày của trang Studio (SPEC Studio mục 5.6, 8.6, 8.8).
// Tháng KC là tháng UTC (kỳ TikTok tính theo UTC+0): trang dùng tháng do API trả; các hàm *Utc ở đây chỉ để
// đặt giới hạn ô chọn ngày (đề nghị nhập KC) và giá trị tạm trước khi API trả `suggestedMonth`.

import dayjs from 'dayjs';

const numberFormat = new Intl.NumberFormat('vi-VN');
const oneDecimal = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 1 });

/** Dấu trừ dùng khi hiện số âm (dễ đọc hơn gạch nối). */
export const MINUS = '−';

const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** Số KC đầy đủ: 16.052.885; null → "—". */
export function formatKc(n?: number | null): string {
  if (!isNum(n)) return '—';
  return n < 0 ? `${MINUS}${numberFormat.format(-n)}` : numberFormat.format(n);
}

/** Số có dấu: +2.881.908 / −2.881.908 / 0. */
export function formatSigned(n?: number | null): string {
  if (!isNum(n)) return '—';
  if (n === 0) return '0';
  return n > 0 ? `+${numberFormat.format(n)}` : `${MINUS}${numberFormat.format(-n)}`;
}

/** Số gọn cho biểu đồ: 850 · 12,5 N · 1,2 tr · 3,4 tỷ (N = nghìn, tr = triệu). */
export function formatCompact(n?: number | null): string {
  if (!isNum(n)) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? MINUS : '';
  if (abs >= 1e9) return `${sign}${oneDecimal.format(abs / 1e9)} tỷ`;
  if (abs >= 1e6) return `${sign}${oneDecimal.format(abs / 1e6)} tr`;
  if (abs >= 1e3) return `${sign}${oneDecimal.format(abs / 1e3)} N`;
  return `${sign}${numberFormat.format(abs)}`;
}

/** % đổi do API trả (đã làm tròn 1 chữ số, mục 5.6): +12,5% / −15,2% / 0%; null → "—". */
export function formatPct(pct?: number | null): string {
  if (!isNum(pct)) return '—';
  if (pct === 0) return '0%';
  const text = oneDecimal.format(Math.abs(pct));
  return pct > 0 ? `+${text}%` : `${MINUS}${text}%`;
}

/** Số giờ LIVE từ số giây: Math.floor(giây / 3600) (mục 5.6). */
export function hoursOf(seconds?: number | null): number | null {
  return isNum(seconds) ? Math.floor(seconds / 3600) : null;
}

/** "12" hoặc "12 giờ" từ số giây; null → "—". */
export function formatHours(seconds?: number | null, withUnit = false): string {
  const hours = hoursOf(seconds);
  if (hours === null) return '—';
  return withUnit ? `${numberFormat.format(hours)} giờ` : numberFormat.format(hours);
}

/** '2026-09' → '09/2026'. */
export function monthLabel(month?: string | null): string {
  if (!month || !/^\d{4}-\d{2}$/.test(month)) return month || '—';
  return `${month.slice(5, 7)}/${month.slice(0, 4)}`;
}

/** '2026-09' → '09' (câu "Tháng MM chưa có dữ liệu"). */
export function monthNumber(month?: string | null): string {
  return month && /^\d{4}-\d{2}/.test(month) ? month.slice(5, 7) : '';
}

/** '2026-09-27' → '27/09' (dòng phụ "đến dd/mm"). */
export function dayMonth(day?: string | null): string {
  if (!day || !/^\d{4}-\d{2}-\d{2}/.test(day)) return '—';
  return `${day.slice(8, 10)}/${day.slice(5, 7)}`;
}

/** 'đến 27/09' hoặc '' khi chưa có ngày. */
export function asOfLabel(day?: string | null): string {
  return day ? `đến ${dayMonth(day)}` : '';
}

/** Ngày lịch 'YYYY-MM-DD' (ngày VN do API trả) → DD/MM/YYYY. */
export function formatDay(day?: string | null): string {
  if (!day || !/^\d{4}-\d{2}-\d{2}/.test(day)) return '—';
  return `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}`;
}

/** Thời điểm ISO → DD/MM/YYYY HH:mm theo giờ máy (nhật ký, đề nghị). */
export function formatDateTime(iso?: string | null): string {
  return iso ? dayjs(iso).format('DD/MM/YYYY HH:mm') : '—';
}

/** "vừa xong", "5 phút trước", "3 giờ trước", "2 ngày trước"; quá 30 ngày thì hiện ngày. */
export function formatAgo(iso?: string | null, now: number = Date.now()): string {
  if (!iso) return '—';
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return '—';
  const minutes = Math.floor(Math.max(0, now - time) / 60000);
  if (minutes < 1) return 'vừa xong';
  if (minutes < 60) return `${minutes} phút trước`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = Math.floor(hours / 24);
  if (days <= 30) return `${days} ngày trước`;
  return dayjs(iso).format('DD/MM/YYYY');
}

/** Ngày UTC hôm nay 'YYYY-MM-DD'. */
export function utcToday(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Tháng UTC hiện tại 'YYYY-MM'. */
export function utcMonth(now: Date = new Date()): string {
  return utcToday(now).slice(0, 7);
}

/** '2026-09' → '2026-08'. */
export function prevMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 2, 1));
  return date.toISOString().slice(0, 7);
}

/** Cộng / trừ ngày trên chuỗi 'YYYY-MM-DD' (theo lịch, không đổi múi giờ). */
export function addDays(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
}

/** UID dài → '…1234' (hiện kèm tên TikTok). */
export function shortUid(uid?: string | null): string {
  return uid ? `…${uid.slice(-4)}` : '';
}

/** Tên hiển thị của một thành viên / ứng viên: tên → @TikTok ID → UID …1234. */
export function displayName(p: {
  nickName?: string | null;
  name?: string | null;
  username?: string | null;
  uid?: string | null;
}): string {
  return (
    p.nickName ||
    p.name ||
    (p.username ? `@${p.username}` : '') ||
    (p.uid ? `UID ${shortUid(p.uid)}` : '') ||
    '—'
  );
}
